import { Stack, Text } from '@mantine/core'
import { faCalendar } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDate, redactPropsWithClassName } from '@phoenix-web/utils/general'
import {
  Audience,
  CLEARED_TO_CLOSE,
  CORRESPONDENT_IN_PROGRESS,
  FINAL_APPROVED,
  FUNDED,
  getProductLabel,
  TransactionType,
  UNDER_CONTRACT,
} from '@phoenix-web/utils/journey'

export const VISIBLE_STEPS = {
  [Audience.AGENT]: [CLEARED_TO_CLOSE, FINAL_APPROVED, FUNDED, UNDER_CONTRACT],
  [Audience.LENDER]: [CLEARED_TO_CLOSE, FINAL_APPROVED, UNDER_CONTRACT],
  [Audience.HOMEBUYER]: [UNDER_CONTRACT, CLEARED_TO_CLOSE, FUNDED, CORRESPONDENT_IN_PROGRESS],
}

const SHOW_BRIDGE_LOAN_CLOSING_DATE_DISCLAIMER: Record<Audience, string[]> = {
  [Audience.AGENT]: [UNDER_CONTRACT, CLEARED_TO_CLOSE],
  [Audience.LENDER]: [UNDER_CONTRACT, CLEARED_TO_CLOSE, FINAL_APPROVED],
  [Audience.HOMEBUYER]: [UNDER_CONTRACT, CLEARED_TO_CLOSE],
}

export default function BoxImportantDates({
  audience,
  currentTimelineStep,
  estimatedClosingDate,
  contractClosingDate,
  loanMaturityDate,
  borrowerDocsDueByDate,
  transactionType,
}: {
  audience: Audience
  currentTimelineStep: string
  estimatedClosingDate?: string
  contractClosingDate?: string
  loanMaturityDate?: string
  borrowerDocsDueByDate?: string
  transactionType?: TransactionType
}) {
  const productLabel = getProductLabel(transactionType)
  return (
    <Stack
      gap="xl"
      style={{ borderRadius: 'var(--mantine-radius-sm)' }}
      bd="1px solid light-gray.6"
      w="100%"
      p="lg"
      pb="xl"
      mih="12.188rem"
    >
      <Text fw={700} size="lg">
        Important Dates
      </Text>

      {(currentTimelineStep === FUNDED || currentTimelineStep === CORRESPONDENT_IN_PROGRESS) &&
      (audience === Audience.AGENT || audience === Audience.HOMEBUYER) ? (
        // Loan Maturity Date is only visible to agents
        <Stack gap={5}>
          <Text fw={700}>Loan Maturity Date</Text>
          <Text>
            <FontAwesomeIcon style={{ marginRight: 'var(--mantine-spacing-sm)' }} size="lg" icon={faCalendar} />
            {loanMaturityDate ? (
              <b {...redactPropsWithClassName}>{formatDate(loanMaturityDate, 'LLLL d, yyyy')}</b>
            ) : (
              <b>--</b>
            )}{' '}
            or upon the sale of the departing property, whichever occurs first.
          </Text>
        </Stack>
      ) : (
        <>
          {borrowerDocsDueByDate && (
            <Stack gap={5}>
              <Text>Final Documents</Text>
              <Text>
                <FontAwesomeIcon style={{ marginRight: 'var(--mantine-spacing-sm)' }} size="lg" icon={faCalendar} />
                Due by{' '}
                {borrowerDocsDueByDate ? (
                  <b {...redactPropsWithClassName}>{formatDate(borrowerDocsDueByDate, 'LLLL d, yyyy')}</b>
                ) : (
                  '--'
                )}
              </Text>
            </Stack>
          )}

          <Stack gap={5}>
            <Text>{productLabel} Closing</Text>
            <Text>
              <FontAwesomeIcon style={{ marginRight: 'var(--mantine-spacing-sm)' }} size="lg" icon={faCalendar} />
              Scheduled for{' '}
              {estimatedClosingDate ? (
                <b {...redactPropsWithClassName}>{formatDate(estimatedClosingDate, 'LLLL d, yyyy')}</b>
              ) : (
                '--'
              )}
            </Text>
          </Stack>

          <Stack gap={5}>
            <Text>Purchase Closing</Text>

            <Text>
              <FontAwesomeIcon style={{ marginRight: 'var(--mantine-spacing-sm)' }} size="lg" icon={faCalendar} />
              Scheduled for{' '}
              {contractClosingDate ? (
                <b {...redactPropsWithClassName}>{formatDate(contractClosingDate, 'LLLL d, yyyy')}</b>
              ) : (
                '--'
              )}
            </Text>
          </Stack>
        </>
      )}

      {(currentTimelineStep === FUNDED || currentTimelineStep === CORRESPONDENT_IN_PROGRESS) &&
        audience === Audience.HOMEBUYER && (
          <Text pt="sm" fz="xs">
            If you decide to exercise the Knock Purchase Offer, Knock Property 1 will, per your signed contract,
            complete a property inspection, and may require repairs completed or price adjusted based on the findings.
          </Text>
        )}

      {/* Show the disclaimer whenever the bridge loan closing date is visible */}
      {SHOW_BRIDGE_LOAN_CLOSING_DATE_DISCLAIMER[audience]?.includes(currentTimelineStep) && (
        <Text pt="sm" fz="xs">
          {productLabel} funds will be sent to the settlement provider. Any delay in the {productLabel} closing will
          delay the purchase closing due to a mandatory 3-day{' '}
          {transactionType === TransactionType.HSEA4 ? 'cooling-off' : 'rescission'} period.
        </Text>
      )}

      {/* Only show on the Funded (Selling) step for agents when the transaction type is not HSEA2 */}
      {currentTimelineStep === FUNDED && audience === Audience.AGENT && transactionType !== TransactionType.HSEA2 && (
        <Text pt="sm" fz="xs">
          If your client decides to exercise the Knock Purchase Offer, Knock Property 1 will, per their signed contract,
          complete a property inspection, and may require repairs completed or price adjusted based on the findings.
        </Text>
      )}
    </Stack>
  )
}
